import React, { useCallback, useEffect, useState } from 'react';
import {
  AvatarGenerateRequest,
  ImageGenHealthStatus,
  creatorChatAdminService,
} from '../../services/creatorChatService';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  fontSize: 13,
  boxSizing: 'border-box',
  color: '#1e293b',
  background: '#fff',
  fontFamily: 'inherit',
};

const selectStyle: React.CSSProperties = { ...inputStyle };

type Props = {
  canManage: boolean;
  currentAvatarUrl: string;
  onPickAvatar: (url: string) => void;
};

const EMPTY: AvatarGenerateRequest = {
  sex: 'female',
  ageLook: '21-25',
  ethnicity: 'European',
  skinTone: '',
  faceShape: '',
  eyeColor: 'brown',
  eyeShape: '',
  eyebrows: '',
  hairColor: 'brunette',
  hairLength: 'long',
  hairStyle: 'wavy',
  facialHair: 'none',
  lips: '',
  makeup: 'natural',
  distinctiveMarks: '',
  expression: 'soft smile',
  bodyType: 'slim',
  heightLook: 'average',
  bustLook: '',
  waistHips: '',
  tattoos: 'none',
  outfitType: 'casual',
  top: '',
  bottom: '',
  outfitColors: '',
  accessories: '',
  framing: 'waist-up',
  pose: 'looking at camera',
  background: 'soft studio',
  lighting: 'soft studio light',
  extraDescription: '',
  avoid: '',
  numImages: 4,
};

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({
  label,
  children,
  hint,
}) => (
  <div style={{ marginBottom: 0 }}>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
      {label}
    </label>
    {children}
    {hint ? <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{hint}</div> : null}
  </div>
);

const AdminCreatorChatAvatarGenerator: React.FC<Props> = ({
  canManage,
  currentAvatarUrl,
  onPickAvatar,
}) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<AvatarGenerateRequest>(EMPTY);
  const [health, setHealth] = useState<ImageGenHealthStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);

  const set = <K extends keyof AvatarGenerateRequest>(key: K, value: AvatarGenerateRequest[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const loadHealth = useCallback(async () => {
    try {
      setHealth(await creatorChatAdminService.imageGenHealth());
    } catch {
      setHealth(null);
    }
  }, []);

  useEffect(() => {
    if (open) void loadHealth();
  }, [open, loadHealth]);

  const generate = async () => {
    if (!canManage) return;
    setBusy(true);
    setMsg(null);
    setCandidates([]);
    setSelected(null);
    try {
      const res = await creatorChatAdminService.generateAvatar({
        ...form,
        numImages: 4,
      });
      const urls = res.imageUrls || [];
      setCandidates(urls);
      if (urls.length === 0) {
        setMsg('No images returned. Try again with a simpler description.');
      } else {
        setMsg(`Generated ${urls.length} option${urls.length === 1 ? '' : 's'}. Click one to use as avatar, then Save.`);
      }
    } catch (e: any) {
      setMsg(e?.message || 'Avatar generation failed');
    } finally {
      setBusy(false);
    }
  };

  const useSelected = () => {
    if (!selected) {
      setMsg('Select an image first.');
      return;
    }
    onPickAvatar(selected);
    setMsg('Avatar set — click Save on the modal to keep it.');
  };

  if (!canManage) return null;

  return (
    <div
      style={{
        marginTop: 12,
        marginBottom: 8,
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        background: '#f8fafc',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        className="admin-btn"
        style={{
          width: '100%',
          borderRadius: 0,
          border: 'none',
          borderBottom: open ? '1px solid #e2e8f0' : 'none',
          background: '#fff',
          textAlign: 'left',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 14px',
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <span>
          <strong>Generate photorealistic avatar</strong>
          <span style={{ display: 'block', fontSize: 11, fontWeight: 400, color: '#64748b', marginTop: 2 }}>
            Describe look → fal Flux creates stills → pick one for later video animation
          </span>
        </span>
        <span style={{ color: '#64748b', fontSize: 12 }}>{open ? 'Hide' : 'Show'}</span>
      </button>

      {open ? (
        <div style={{ padding: 14 }}>
          {health && !health.ok ? (
            <p
              style={{
                fontSize: 12,
                color: '#b45309',
                margin: '0 0 12px',
                background: '#fffbeb',
                padding: 8,
                borderRadius: 8,
              }}
            >
              AI avatar generation unavailable: {health.message || 'Configure fal API key on the server.'}
            </p>
          ) : null}
          {health?.ok ? (
            <p style={{ fontSize: 11, color: '#047857', margin: '0 0 12px' }}>
              Ready ({health.model}). Adults 21+ only. Costs fal credits.
            </p>
          ) : null}

          <div className="creator-chat-avatar-gen-grid">
            <Field label="Sex / gender presentation *">
              <select style={selectStyle} value={form.sex} onChange={(e) => set('sex', e.target.value)}>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="androgynous">Androgynous</option>
              </select>
            </Field>
            <Field label="Age look * (adults only)">
              <select style={selectStyle} value={form.ageLook} onChange={(e) => set('ageLook', e.target.value)}>
                <option value="21-25">21–25</option>
                <option value="26-30">26–30</option>
                <option value="31-35">31–35</option>
                <option value="36-40">36–40</option>
                <option value="41-50">41–50</option>
                <option value="50+">50+</option>
              </select>
            </Field>
            <Field label="Ethnicity / region *">
              <select style={selectStyle} value={form.ethnicity} onChange={(e) => set('ethnicity', e.target.value)}>
                <option value="European">European</option>
                <option value="Mediterranean">Mediterranean</option>
                <option value="East Asian">East Asian</option>
                <option value="South Asian">South Asian</option>
                <option value="Southeast Asian">Southeast Asian</option>
                <option value="Middle Eastern">Middle Eastern</option>
                <option value="African">African</option>
                <option value="Afro-Caribbean">Afro-Caribbean</option>
                <option value="Latina / Latino">Latina / Latino</option>
                <option value="Mixed heritage">Mixed heritage</option>
              </select>
            </Field>
            <Field label="Skin tone">
              <select style={selectStyle} value={form.skinTone || ''} onChange={(e) => set('skinTone', e.target.value)}>
                <option value="">Default for ethnicity</option>
                <option value="fair">Fair</option>
                <option value="light">Light</option>
                <option value="medium">Medium</option>
                <option value="olive">Olive</option>
                <option value="tan">Tan</option>
                <option value="brown">Brown</option>
                <option value="deep">Deep</option>
              </select>
            </Field>
            <Field label="Hair color *">
              <select style={selectStyle} value={form.hairColor} onChange={(e) => set('hairColor', e.target.value)}>
                <option value="black">Black</option>
                <option value="brunette">Brunette</option>
                <option value="light brown">Light brown</option>
                <option value="blonde">Blonde</option>
                <option value="platinum blonde">Platinum blonde</option>
                <option value="redhead">Redhead</option>
                <option value="auburn">Auburn</option>
                <option value="silver">Silver</option>
                <option value="pastel dyed">Pastel dyed</option>
              </select>
            </Field>
            <Field label="Hair length *">
              <select style={selectStyle} value={form.hairLength} onChange={(e) => set('hairLength', e.target.value)}>
                <option value="short">Short</option>
                <option value="medium">Medium</option>
                <option value="long">Long</option>
                <option value="very long">Very long</option>
              </select>
            </Field>
            <Field label="Hair style *">
              <select style={selectStyle} value={form.hairStyle} onChange={(e) => set('hairStyle', e.target.value)}>
                <option value="straight">Straight</option>
                <option value="wavy">Wavy</option>
                <option value="curly">Curly</option>
                <option value="coily">Coily</option>
                <option value="ponytail">Ponytail</option>
                <option value="bun">Bun</option>
                <option value="braids">Braids</option>
                <option value="with bangs">With bangs</option>
              </select>
            </Field>
            <Field label="Eye color *">
              <select style={selectStyle} value={form.eyeColor} onChange={(e) => set('eyeColor', e.target.value)}>
                <option value="brown">Brown</option>
                <option value="hazel">Hazel</option>
                <option value="green">Green</option>
                <option value="blue">Blue</option>
                <option value="grey">Grey</option>
                <option value="amber">Amber</option>
              </select>
            </Field>
            <Field label="Body type *">
              <select style={selectStyle} value={form.bodyType} onChange={(e) => set('bodyType', e.target.value)}>
                <option value="slim">Slim</option>
                <option value="athletic">Athletic</option>
                <option value="curvy">Curvy</option>
                <option value="hourglass">Hourglass</option>
                <option value="plus-size">Plus-size</option>
                <option value="muscular">Muscular</option>
              </select>
            </Field>
            <Field label="Outfit type *">
              <select style={selectStyle} value={form.outfitType} onChange={(e) => set('outfitType', e.target.value)}>
                <option value="casual">Casual</option>
                <option value="elegant">Elegant</option>
                <option value="streetwear">Streetwear</option>
                <option value="sporty">Sporty</option>
                <option value="evening dress">Evening dress</option>
                <option value="business casual">Business casual</option>
                <option value="tasteful lingerie">Tasteful lingerie</option>
                <option value="bikini">Bikini</option>
              </select>
            </Field>
            <Field label="Framing *">
              <select style={selectStyle} value={form.framing} onChange={(e) => set('framing', e.target.value)}>
                <option value="close-up">Close-up (head &amp; shoulders)</option>
                <option value="waist-up">Waist-up (best for chat)</option>
                <option value="full-body">Full body (best for later actions)</option>
              </select>
            </Field>
            <Field label="Expression">
              <select
                style={selectStyle}
                value={form.expression || ''}
                onChange={(e) => set('expression', e.target.value)}
              >
                <option value="soft smile">Soft smile</option>
                <option value="neutral">Neutral</option>
                <option value="flirty smile">Flirty smile</option>
                <option value="confident">Confident</option>
                <option value="serious">Serious</option>
              </select>
            </Field>
            <Field label="Makeup">
              <select style={selectStyle} value={form.makeup || ''} onChange={(e) => set('makeup', e.target.value)}>
                <option value="none">None</option>
                <option value="natural">Natural</option>
                <option value="glam">Glam</option>
                <option value="smoky eyes">Smoky eyes</option>
              </select>
            </Field>
            <Field label="Background">
              <select
                style={selectStyle}
                value={form.background || ''}
                onChange={(e) => set('background', e.target.value)}
              >
                <option value="soft studio">Soft studio</option>
                <option value="plain light grey">Plain light grey</option>
                <option value="bedroom soft blur">Bedroom soft blur</option>
                <option value="outdoor golden hour">Outdoor golden hour</option>
                <option value="luxury interior">Luxury interior</option>
              </select>
            </Field>
            <Field label="Pose">
              <input
                style={inputStyle}
                value={form.pose || ''}
                onChange={(e) => set('pose', e.target.value)}
                placeholder="looking at camera"
                maxLength={80}
              />
            </Field>
            <Field label="Outfit colors">
              <input
                style={inputStyle}
                value={form.outfitColors || ''}
                onChange={(e) => set('outfitColors', e.target.value)}
                placeholder="e.g. black and gold"
                maxLength={80}
              />
            </Field>
            <Field label="Accessories">
              <input
                style={inputStyle}
                value={form.accessories || ''}
                onChange={(e) => set('accessories', e.target.value)}
                placeholder="earrings, necklace…"
                maxLength={120}
              />
            </Field>
          </div>

          <div style={{ marginTop: 12 }}>
            <Field label="Extra description" hint="Optional details (adult 21+ only).">
              <textarea
                style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
                value={form.extraDescription || ''}
                onChange={(e) => set('extraDescription', e.target.value)}
                placeholder="e.g. dimple on left cheek, gold hoop earrings, teal dress…"
                maxLength={300}
              />
            </Field>
          </div>
          <div style={{ marginTop: 10 }}>
            <Field label="Avoid (optional)">
              <input
                style={inputStyle}
                value={form.avoid || ''}
                onChange={(e) => set('avoid', e.target.value)}
                placeholder="glasses, tattoos, heavy makeup…"
                maxLength={200}
              />
            </Field>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14, alignItems: 'center' }}>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              disabled={busy || (health != null && !health.ok)}
              onClick={() => void generate()}
            >
              {busy ? 'Generating… (30–90s)' : 'Generate 4 avatars'}
            </button>
            {selected ? (
              <button type="button" className="admin-btn" disabled={busy} onClick={useSelected}>
                Use selected as avatar
              </button>
            ) : null}
            {currentAvatarUrl ? (
              <span style={{ fontSize: 11, color: '#64748b' }}>Current avatar is set</span>
            ) : null}
          </div>

          {msg ? (
            <p
              style={{
                margin: '12px 0 0',
                fontSize: 12,
                color: '#334155',
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: 8,
              }}
            >
              {msg}
            </p>
          ) : null}

          {candidates.length > 0 ? (
            <div className="creator-chat-avatar-candidates" style={{ marginTop: 12 }}>
              {candidates.map((url) => {
                const isSel = selected === url || currentAvatarUrl === url;
                return (
                  <button
                    key={url}
                    type="button"
                    className={`creator-chat-avatar-candidate${isSel ? ' selected' : ''}`}
                    onClick={() => {
                      setSelected(url);
                      onPickAvatar(url);
                      setMsg('Avatar set — click Save on the modal to keep it.');
                    }}
                    style={{
                      border: isSel ? '2px solid #086afb' : '2px solid #e2e8f0',
                      borderRadius: 10,
                      overflow: 'hidden',
                      padding: 0,
                      background: '#0f172a',
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={url}
                      alt="Generated avatar option"
                      style={{ width: '100%', height: 180, objectFit: 'contain', display: 'block' }}
                    />
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default AdminCreatorChatAvatarGenerator;
